import React, { forwardRef, useEffect, useRef, useState } from "react";
import style from "./TeamLiderPlaceHolder.module.scss";
import gsap from "gsap";
import { Icon } from "@/components/common/Icons/Icon";
import { useGSAP } from "@gsap/react";
import { MagneticCursor } from "@/components/common/MagneticCursor/MagneticCursor";
import IsologoTransition from "@/components/common/IsologoTransition/IsologoTransition";
import { API_BASE } from "../../../constants/config";
import { TitleAnimation } from "@/components/common/TitleAnimation/TitleAnimation";
import { usePathname } from "next/navigation";
import { useScrollStore } from "@/components/common/ScrollManager/useScrollStore";

interface CardProps {
  messages: {
    first_word_leader: string;
    second_word_leader: string;
    description: string;
    team: Team[];
  };
}
interface Team {
  name: string;
  horizontal_name: string;
  rol: string;
  description: string;
  linkedin: string;
  metrics?: string[];
  image: string;
  horizontal_image?: string;
}

export default function TeamLiderPlaceHolder({ messages }: CardProps) {
  const pathname = usePathname();
  const hrefAbout = pathname === "/nosotros" || pathname === "/en/about";
  const [open, setOpen] = useState(false);
  const [hasOpen, setHasOpen] = useState(false);
  const { scroller } = useScrollStore();

  useEffect(() => {
    if (window.innerWidth >= 1000) {
      if (open) {
        if (!hasOpen) {
          setHasOpen(true);
        }
        const container = document.querySelector(`.${style.container}`);
        container.scrollIntoView({ block: "center", behavior: "smooth" });

        scroller?.stop();
      } else {
        scroller?.start();
      }
    } else {
      if (open) {
        if (!hasOpen) {
          setHasOpen(true);
        }

        scroller?.stop();
      } else {
        scroller?.start();
      }
    }

    return () => {
      scroller?.start();
    };
  }, [open, hasOpen]);
  const sliceTeam = messages?.team?.slice(0, 6);

  return (
    <div className={`${style.section}`}>
      <IsologoTransition extraOffset={hrefAbout} isPin isSmoothPin />
      <div className={`${style.container} container`}>
        <div
          className={`${style.overlay} ${open ? style.heightMobile : ""}`}
        ></div>
        <div className={`${style.cardActivePos} cardActivePos`}>
          <HorizontalCards setOpen={setOpen} team={sliceTeam} />
        </div>
        <div className={style.titleContainer}>
          <TitleAnimation
            messages={{
              first_word: messages.first_word_leader,
              second_word: messages.second_word_leader,
            }}
          />
          <p className={style.description}>{messages.description}</p>
        </div>
        <div className={`${style.cardsContainer}`}>
          {sliceTeam.map((item, index) => (
            <div
              className={`${style.cardPlaceholder} cardPlaceholder${index}`}
              key={index}
            >
              <Card
                img={item.image}
                name={item.name}
                role={item.rol}
                class={`card${index} card`}
                key={index}
                styles={{}}
                setOpen={setOpen}
                open={open}
                attrValue={index}
              ></Card>
            </div>
          ))}
          {/* <div className={style.mobileBTNs}>
            <div
              className={style.leftBTN}
              onClick={() => handleCards("left")}
            ></div>
            <div
              className={style.rightBTN}
              onClick={() => handleCards("right")}
            ></div>
          </div> */}
        </div>
      </div>
    </div>
  );
}

const Card = forwardRef(
  (
    props: {
      name: string;
      role: string;
      img: string;
      class: string;
      styles: React.CSSProperties;
      setOpen: any;
      open: boolean;
      attrValue: number;
    },
    ref
  ) => {
    const card = useRef(null);
    const cardBack = useRef(null);
    const front = useRef(null);
    const glow = useRef(null);
    let { setOpen, open } = props;
    const [innerOpen, setInnerOpen] = useState(false);

    useEffect(() => {
      if (!open) {
        setInnerOpen(false);
      }
    }, [open]);

    useEffect(() => {
      const hoverIn = () => {
        if (window.innerWidth < 1000) return;

        const tl = gsap.timeline();
        tl.to(`.cardPlaceholder${props.attrValue}`, {
          zIndex: 20,
          duration: 0,
        })
          .to(
            front.current,
            {
              scale: 1.1,
              rotate: 5,
              duration: 0.3,
              ease: "power4.out",
            },
            ">"
          )
          .to(
            glow.current,
            {
              scale: 1.1,
              rotate: 5,
              duration: 0.3,
              ease: "power4.out",
            },
            "<"
          );
      };

      const hoverOut = () => {
        if (window.innerWidth < 1000) return;

        const tl = gsap.timeline();
        tl.to(front.current, {
          scale: 1,
          rotate: 0,
          duration: 0.3,
          ease: "power4.in",
        }).to(
          glow.current,
          {
            scale: 1,
            rotate: 0,
            duration: 0.3,
            ease: "power4.in",
          },
          "<"
        );

        if (!innerOpen) {
          tl.to(
            `.cardPlaceholder${props.attrValue}`,
            {
              zIndex: 0,
              duration: 0,
            },
            ">"
          );
        }
      };

      const frontElement = front.current;

      if (frontElement) {
        frontElement.addEventListener("mouseenter", hoverIn);
        frontElement.addEventListener("mouseleave", hoverOut);
        return () => {
          frontElement.removeEventListener("mouseenter", hoverIn);
          frontElement.removeEventListener("mouseleave", hoverOut);
        };
      }
    }, [open]);

    useEffect(() => {
      if (!innerOpen) {
        gsap.set(`.cardPlaceholder${props.attrValue}`, { zIndex: 0 });
      }
    }, [innerOpen]);

    useGSAP(() => {
      const isMobileView = window.innerWidth < 1000;

      if (isMobileView) {
        const tl = gsap.timeline();
        if (innerOpen) {
          tl.to(`.${style.overlay}`, { opacity: 1, zIndex: 110, duration: 0.15 })
            .set(`.hcard${props.attrValue}`, { display: "block", scale: 0.9, opacity: 0 })
            .to(`.hcard${props.attrValue}`, {
              scale: 1,
              opacity: 1,
              pointerEvents: "all",
              duration: 0.25,
              ease: "power2.out",
            });
        } else {
          tl.to(`.hcard${props.attrValue}`, {
            scale: 0.9,
            opacity: 0,
            pointerEvents: "none",
            duration: 0.2,
            ease: "power2.in",
          })
            .set(`.hcard${props.attrValue}`, { display: "none", scale: 1 })
            .to(`.${style.overlay}`, { opacity: 0, zIndex: -1, duration: 0.15 });
        }
        return;
      }

      const { height, top, x } = card.current.getBoundingClientRect();
      const sBounds = window.matchMedia("(max-width: 1600px)").matches
        ? document
            .getElementsByClassName(style.cardsContainer)[0]
            .getBoundingClientRect()
        : card.current.offsetParent.getBoundingClientRect();

      const {
        width: finalPosRefWidth,
        height: finalPosRefHeight,
        x: finalPosRefX,
        y: finalPosRefY,
      } = document
        .getElementsByClassName("cardActivePos")[0]
        .getBoundingClientRect();

      // Height diff btw section top and horizontal card position top
      const heightDiff =
        top < finalPosRefY
          ? Math.abs(sBounds.top - finalPosRefY)
          : -height - 12 - (sBounds.top - finalPosRefY);
      // Left diff btw card and hcard
      const leftDiff = x - finalPosRefX;

      const isMobile = window.innerWidth < 1600;

      const mobileHeightDiff =
        top < finalPosRefY
          ? Math.abs(top - finalPosRefY)
          : -(top - finalPosRefY);

      const tl = gsap.timeline();
      if (!card.current) return;
      if (innerOpen) {
        gsap.set(`.${style.cardPlaceholder}`, { zIndex: 0 });

        tl.to(card.current, {
          rotateY: -180,
          zIndex: 115,
          ease: "power4.in",
          duration: 0.25,
        })
          .to(`.cardPlaceholder${props.attrValue}`, {
            zIndex: 115,
            duration: 0,
          })
          .to(`.${style.overlay}`, {
            opacity: 1,
            zIndex: 110,
            duration: 0.05,
          })
          .to(card.current, {
            left: -leftDiff,
            top: isMobile ? mobileHeightDiff : heightDiff,
            ease: "power4.in",
            duration: 0.175,
          })
          .to(card.current, {
            width: finalPosRefWidth,
            height: finalPosRefHeight,
            pointerEvents: "none",
            "--card-bradius": 60,
            duration: 0.35,
            delay: 0.125,
          })
          .to(`.hcard${props.attrValue}`, {
            display: "block",
            duration: 0,
          })
          .to(
            `.hcard${props.attrValue}`,
            {
              opacity: 1,
              pointerEvents: "all",
              duration: 0.25,
            },
            "<+100%"
          )
          .to(
            card.current,
            {
              "--before-height": "101%",
              "--before-op": "1",
            },
            "end"
          );
      } else if (!innerOpen) {
        tl.to(
          card.current,
          {
            "--before-height": "101%",
            "--before-op": "1",
            "--card-bradius": "38px",

            duration: 0.01,
          },
          "initial"
        )
          .to(
            `.hcard${props.attrValue}`,
            {
              opacity: 0,
              pointerEvents: "none",
              duration: 0.01,
            },
            "initial"
          )
          .to(`.hcard${props.attrValue}`, {
            display: "none",
            duration: 0,
          })
          .to(
            card.current,
            {
              width: "100%",
              height: "100%",
              duration: 0.25,
            },
            "middle"
          )
          .to(
            card.current,
            {
              top: 0,
              left: 0,
              duration: 0.25,
            },
            "middle"
          )

          .to(`.${style.overlay}`, {
            opacity: 0,
            zIndex: -1,
            duration: 0.05,
          })
          .to(card.current, {
            pointerEvents: "all",
            rotateY: 0,
            zIndex: 0,
            duration: 0.25,
          });
      }
    }, [innerOpen]);

    return (
      <div
        className={`${style.card} ${props.class} `}
        style={props.styles}
        onClick={() => {
          if (!open) {
            setOpen(true);
            setInnerOpen(true);
            gsap.set(glow.current, {
              scale: 1,
              rotate: 0,
            });
          }
        }}
        ref={card}
        data-card={props.attrValue}
      >
        <div className={style.front} ref={front}>
          <div className={style.cardContent}>
            <div className={style.textContainer}>
              <h2>{props.name}</h2>
              <p>{props.role}</p>
            </div>
            <picture className={style.imageContainer}>
              <img src={API_BASE + props.img} alt={`Image of ${props.name}`} />
            </picture>
          </div>
        </div>
        <div className={`${style.back}`} ref={cardBack}></div>
        <div className={`${style.glow}`} ref={glow}></div>
      </div>
    );
  }
);
Card.displayName = "Card";

const HorizontalCards = ({ setOpen, team }: { setOpen: any; team: any }) => {
  function handleHCardClick(index: number) {
    if (window.innerWidth < 1000) {
      setOpen(false);
      return;
    }

    const card = document.getElementsByClassName("hcard" + index)[0];
    const tl = gsap.timeline();

    tl.to(card, {
      opacity: 0,
      pointerEvents: "none",
      display: "none",
      onComplete: () => {
        setOpen(false);
      },
    });
  }
  return (
    <>
      {team.map((item, index) => (
        <div
          className={`${style.horizontalCard} hcard${index}`}
          key={index}
          data-hover={"hcards"}
          onClick={() => handleHCardClick(index)}
        >
          <div className={style.horizontalCard_cardContent}>
            <Icon name={"CloseButton"} />
            <div className={style.horizontalCard_textContainer}>
              <div className={style.horizontalCard_textContentTop}>
                <h2>{item.horizontal_name}</h2>
                <p className={style.horizontalCard_role}>{item.rol}</p>
                <p>{item.description}</p>
                {item.linkedin && (
                  <MagneticCursor>
                    <div
                      className={style.horizontalCard_socialContainer}
                      data-hover={"hcards-social"}
                    >
                      <a href={item.linkedin} target="_blank">
                        <Icon name={"LinkedIn"} fill="var(--secondary)" />
                      </a>
                    </div>
                  </MagneticCursor>
                )}
              </div>
              <div className={style.horizontalCard_textContentBottom}>
                {item?.metrics?.map((stat, index) => (
                  <div className={style.horizontalCard_stats} key={index}>
                    <h3>
                      <span>{stat[0].slice(0, 1)}</span>
                      {stat[0].slice(1)}
                    </h3>
                    <p>{stat[1]}</p>
                  </div>
                ))}
              </div>
            </div>
            <picture className={style.horizontalCard_imageContainer}>
              <img
                src={API_BASE + item.horizontal_image}
                alt={`Image of ${item.name}`}
              />
            </picture>
          </div>
        </div>
      ))}
    </>
  );
};
